import React, { createContext, useState, useContext, useEffect } from 'react';
import { getUIText } from '../utils/uiTranslations';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'zh', label: 'Chinese', native: '中文' },
];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('site_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('site_language', language);
  }, [language]);

  const setLanguage = (code) => {
    if (SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
      setLanguageState(code);
    }
  };

  // Returns item[fieldName] translated into the current language if a
  // translation exists, otherwise falls back to the original
  // (English) value -- content is never machine-translated for
  // every English speaker; this only ever swaps in a translation
  // that an admin has actually generated for that specific item.
  const getTranslatedField = (item, fieldName) => {
    if (!item) return '';
    if (language === 'en') return item[fieldName] || '';
    const translated = item.translations?.[language]?.[fieldName];
    return translated || item[fieldName] || '';
  };

  // Looks up fixed UI text (nav labels, common buttons) -- separate
  // from getTranslatedField above, which handles admin-entered
  // content. See utils/uiTranslations.js for the dictionary.
  const t = (key) => getUIText(key, language);

  const value = { language, setLanguage, getTranslatedField, t, languages: SUPPORTED_LANGUAGES };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageContext;
